/** App Suites catalogs. Item ids are permanent; the drift test reads this file. */

import {
  Activity,
  Bot,
  Brain,
  Compass,
  Database,
  Gauge,
  Layers,
  LayoutDashboard,
  Lightbulb,
  Network,
  Rocket,
  Shield,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { logger } from '@utils/logger'
import {
  DATAFLOW_NAV_SECTIONS,
  MILKYWAY_NAV_SECTIONS,
  NAV_SECTIONS,
  type NavItem,
} from './navigation'
import type { AppIconKey, NavItemId } from '../types/appSuites'

export const EXPERIENCE_STORAGE_KEY = 'tagp-experience'
export const ACTIVE_APP_SESSION_KEY = 'tagp-active-app'
export const SUITE_CACHE_KEY = 'tagp-app-suites-cache-v1'

export const RESERVED_ITEM_IDS: ReadonlySet<NavItemId> = new Set([
  'home',
  'agent_store',
  'workflow_store',
  'docs',
  'admin',
  'platform_admin',
])

/** Shown inside Agent Observability. Not part of the Classic sidebar. */
export const APPS_ONLY_NAV_ITEMS: NavItem[] = [
  {
    itemId: 'overview',
    labelKey: 'nav.overview',
    path: '/home',
    icon: LayoutDashboard,
    featureFlag: 'home_view',
    featureId: 'home',
  },
]

export const APP_ICON_REGISTRY: Record<AppIconKey, LucideIcon> = {
  network: Network,
  lightbulb: Lightbulb,
  bot: Bot,
  activity: Activity,
  layers: Layers,
  sparkles: Sparkles,
  database: Database,
  brain: Brain,
  shield: Shield,
  rocket: Rocket,
  compass: Compass,
  gauge: Gauge,
}

function collectItems(): Record<NavItemId, NavItem> {
  const map = {} as Record<NavItemId, NavItem>
  const sections = [...NAV_SECTIONS, ...MILKYWAY_NAV_SECTIONS, ...DATAFLOW_NAV_SECTIONS]
  const items = sections.flatMap((section) => section.items).concat(APPS_ONLY_NAV_ITEMS)
  for (const item of items) {
    const existing = map[item.itemId]
    if (existing && existing.path !== item.path) {
      logger.error('app_suites: duplicate nav itemId with a different path', {
        itemId: item.itemId,
        path: item.path,
        existingPath: existing.path,
      })
    }
    map[item.itemId] = item
  }
  return map
}

export const NAV_ITEM_BY_ID: Record<NavItemId, NavItem> = collectItems()

export function navPathById(): Record<string, string> {
  const paths: Record<string, string> = {}
  for (const [itemId, item] of Object.entries(NAV_ITEM_BY_ID)) {
    paths[itemId] = item.path
  }
  return paths
}
